import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, Form, Input, TimePicker, message, Row, Col, Space, Typography, DatePicker, Popover, Flex, List, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EnvironmentOutlined, FieldTimeOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { scheduleApi } from '../api/schedule';
import { DoctorSchedule, ScheduleLocation } from '../api/model';
import dayjs, { Dayjs } from 'dayjs';

const { Title, Text } = Typography;

interface ScheduleFormValues {
  schedule_name: string;
  start_time: Dayjs;
  end_time: Dayjs;
  capacity_initial: number;
  schedule_location_id?: string;
}

interface SchedulePreviewListProps {
  schedules: Partial<DoctorSchedule>[];
}

const SchedulePreviewList: React.FC<SchedulePreviewListProps> = ({ schedules }) => {
  return (
    <List
      size="small"
      bordered
      style={{ maxHeight: "60vh", overflow: "auto" }}
      dataSource={schedules}
      renderItem={(schedule, index) => {
        const startTime = dayjs(schedule.start_time);
        const endTime = dayjs(schedule.end_time);
        return (
          <List.Item>
            <Text strong>{startTime.format('YYYY-MM-DD')} {startTime.format('ddd')}</Text>
            <Text>{schedule.schedule_name}</Text>
            <Text type="secondary">{startTime.format('HH:mm')} - {endTime.format('HH:mm')}</Text>
            <Text type="secondary">可预约：{schedule.capacity_initial}人</Text>
          </List.Item>
        );
      }}
    />
  );
};


const Schedules: React.FC = () => {
  const [schedules, setSchedules] = useState<DoctorSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [batchDeleteModalVisible, setBatchDeleteModalVisible] = useState(false);
  const [templateGenerateModalVisible, setTemplateGenerateModalVisible] = useState(false);
  const [currentSchedule, setCurrentSchedule] = useState<DoctorSchedule | null>(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs()); // 用于创建日程时记录当前日期
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(dayjs()); // 当前显示的月份
  const [schedulesToDelete, setSchedulesToDelete] = useState<DoctorSchedule[]>([]);
  const [confirmDeleteModalVisible, setConfirmDeleteModalVisible] = useState(false);
  const [templateSchedules, setTemplateSchedules] = useState<Partial<DoctorSchedule>[]>([]);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [scheduleLocations, setScheduleLocations] = useState<ScheduleLocation[]>([]);
  const [form] = Form.useForm<ScheduleFormValues>();
  const [updateForm] = Form.useForm<ScheduleFormValues>();
  const [batchDeleteForm] = Form.useForm();
  const [templateGenerateForm] = Form.useForm();

  // 获取日程列表
  const fetchSchedules = async () => {
    setLoading(true);
    try {
      // 计算月份的开始和结束时间
      const startOfMonth = currentMonth.startOf('month').toISOString();
      const endOfMonth = currentMonth.endOf('month').toISOString();
      
      const response = await scheduleApi.listDoctorSchedules({
        start_time: startOfMonth,
        end_time: endOfMonth
      });
      
      if (response.success) {
        // 按日期和开始时间排序
        const sortedSchedules = response.data.sort((a, b) => {
          if (a.start_time !== b.start_time) {
            return a.start_time.localeCompare(b.start_time);
          }
          return 0;
        });
        setSchedules(sortedSchedules);
      } else {
        message.error(response.message || '获取日程失败');
      }
    } catch (error) {
      console.error('获取日程失败:', error);
      message.error('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 获取日程地点列表
  const fetchScheduleLocations = async () => {
    try {
      const response = await scheduleApi.listScheduleLocations();
      if (response.success) {
        setScheduleLocations(response.data);
      } else {
        message.error(response.message || '获取日程地点失败');
      }
    } catch (error) {
      console.error('获取日程地点失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 提交创建表单
  const handleCreateSubmit = async (values: ScheduleFormValues) => {
    try {
      // 格式化时间 (使用ISO8601格式)
      const startTime = dayjs(selectedDate.format('YYYY-MM-DD') + ' ' + values.start_time.format('HH:mm:ss')).toISOString();
      const endTime = dayjs(selectedDate.format('YYYY-MM-DD') + ' ' + values.end_time.format('HH:mm:ss')).toISOString();
      
      const requestData = {
        schedule_name: values.schedule_name,
        start_time: startTime,
        end_time: endTime,
        capacity_initial: +values.capacity_initial,
        schedule_location_id: values.schedule_location_id || null,
      };

      const response = await scheduleApi.createDoctorSchedule(requestData);
      if (response.success) {
        message.success('日程创建成功');
        setCreateModalVisible(false);
        form.resetFields();
        // 重新获取日程列表
        fetchSchedules();
      } else {
        message.error(response.message || '创建日程失败');
      }
    } catch (error) {
      console.error('创建日程失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 提交更新表单
  const handleUpdateSubmit = async (values: ScheduleFormValues) => {
    if (!currentSchedule) return;
    
    try {
      // 格式化时间 (使用ISO8601格式)
      // 从当前日程中提取日期
      const scheduleDate = dayjs(currentSchedule.start_time).format('YYYY-MM-DD');
      const startTime = dayjs(scheduleDate + ' ' + values.start_time.format('HH:mm:ss')).toISOString();
      const endTime = dayjs(scheduleDate + ' ' + values.end_time.format('HH:mm:ss')).toISOString();
      
      const requestData = {
        schedule_name: values.schedule_name,
        start_time: startTime,
        end_time: endTime,
        capacity_initial: +values.capacity_initial,
        schedule_location_id: values.schedule_location_id || null,
      };

      const response = await scheduleApi.updateDoctorSchedule(currentSchedule.id, requestData);
      if (response.success) {
        message.success('日程更新成功');
        setUpdateModalVisible(false);
        updateForm.resetFields();
        setCurrentSchedule(null);
        // 重新获取日程列表
        fetchSchedules();
      } else {
        message.error(response.message || '更新日程失败');
      }
    } catch (error) {
      console.error('更新日程失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 删除日程
  const handleDelete = async (scheduleId: string) => {
    try {
      const response = await scheduleApi.deleteDoctorSchedule(scheduleId);
      if (response.success) {
        message.success('日程删除成功');
        // 重新获取日程列表
        fetchSchedules();
      } else {
        message.error(response.message || '删除日程失败');
      }
    } catch (error) {
      console.error('删除日程失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 获取指定日期范围内的日程
  const fetchSchedulesByDateRange = async (startDate: Dayjs, endDate: Dayjs) => {
    try {
      const response = await scheduleApi.listDoctorSchedules({
        start_time: startDate.startOf('day').toISOString(),
        end_time: endDate.endOf('day').toISOString()
      });
      
      if (response.success) {
        return response.data;
      } else {
        message.error(response.message || '获取日程失败');
        return [];
      }
    } catch (error) {
      console.error('获取日程失败:', error);
      message.error('网络错误，请稍后重试');
      return [];
    }
  };

  // 筛选没有关联预约的日程
  const filterSchedulesWithoutAppointments = (schedules: DoctorSchedule[]) => {
    return schedules.filter(schedule => schedule.capacity_initial === schedule.capacity_left);
  };

  // 处理批量删除确认
  const handleBatchDeleteConfirm = async () => {
    try {
      const scheduleIds = schedulesToDelete.map(schedule => schedule.id);
      const response = await scheduleApi.batchDeleteDoctorSchedules({ ids: scheduleIds });
      
      if (response.success) {
        message.success('批量删除成功');
        setConfirmDeleteModalVisible(false);
        setBatchDeleteModalVisible(false);
        // 重新获取日程列表
        fetchSchedules();
      } else {
        message.error(response.message || '批量删除失败');
      }
    } catch (error) {
      console.error('批量删除失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 获取排班模板列表
  const fetchScheduleTemplates = async () => {
    try {
      const response = await scheduleApi.listDoctorScheduleTemplates();
      
      if (response.success) {
        return response.data;
      } else {
        message.error(response.message || '获取排班模板失败');
        return [];
      }
    } catch (error) {
      console.error('获取排班模板失败:', error);
      message.error('网络错误，请稍后重试');
      return [];
    }
  };

  // 根据模板预览排班
  const handlePreviewByTemplate = async () => {
    try {
      const values = await templateGenerateForm.validateFields();
      const startDate: Dayjs = values.dateRange[0];
      const endDate: Dayjs = values.dateRange[1];
      
      if (startDate && endDate) {
        // 调用接口获取根据模板创建的排班预览
        // 这里需要一个预览接口，暂时用实际创建接口的逻辑来模拟
        // 实际开发中应该有一个专门的预览接口
        
        // 获取模板列表
        const templates = await fetchScheduleTemplates();
        
        // 模拟根据模板生成排班的逻辑
        const generatedSchedules: Partial<DoctorSchedule>[] = [];
        
        // 计算日期范围内的所有日期
        let currentDate = startDate.clone();
        let temp_id = 1;
        while (currentDate.isSameOrBefore(endDate, 'day')) {
          // 根据星期几匹配模板
          const weekday = currentDate.day(); // 0是周日，1是周一，...，6是周六
          
          // 查找匹配的模板
          const matchingTemplates = templates.filter(template => template.weekday === weekday);
          
          // 为每个匹配的模板生成排班
          matchingTemplates.forEach(template => {
            const scheduleDate = currentDate.format('YYYY-MM-DD');
            const startTime = dayjs(scheduleDate + 'T' + template.start_time).toISOString();
            const endTime = dayjs(scheduleDate + 'T' + template.end_time).toISOString();
            
            // 创建模拟的排班对象
            const schedule: Partial<DoctorSchedule> = {
              id: `temp_${temp_id++}`, // 临时ID
              schedule_name: template.slot_name,
              start_time: startTime,
              end_time: endTime,
              capacity_initial: template.capacity,
              capacity_left: template.capacity,
            };
            
            generatedSchedules.push(schedule);
          });
          
          currentDate = currentDate.add(1, 'day');
        }
        
        setTemplateSchedules(generatedSchedules);
        setPreviewModalVisible(true);
      }
    } catch (error) {
      console.error('预览排班失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 根据模板创建排班
  const handleCreateByTemplate = async () => {
    try {
      const values = await templateGenerateForm.validateFields();
      const startDate = values.dateRange[0];
      const endDate = values.dateRange[1];
      
      if (startDate && endDate) {
        const requestData = {
          start_date: startDate.format('YYYY-MM-DD'),
          end_date: endDate.format('YYYY-MM-DD')
        };
        
        const response = await scheduleApi.provisionSchedulesByTemplate(requestData);
        
        if (response.success) {
          message.success('根据模板创建排班成功');
          setTemplateGenerateModalVisible(false);
          setPreviewModalVisible(false); // 添加这行来关闭预览模态框
          templateGenerateForm.resetFields();
          // 重新获取日程列表
          fetchSchedules();
        } else {
          message.error(response.message || '根据模板创建排班失败');
        }
      }
    } catch (error) {
      console.error('根据模板创建排班失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 初始化获取数据
  useEffect(() => {
    fetchSchedules();
    fetchScheduleLocations();
  }, [currentMonth]);

  // 打开更新模态框
  const openUpdateModal = (schedule: DoctorSchedule) => {
    setCurrentSchedule(schedule);
    setUpdateModalVisible(true);
    // 设置表单初始值
    updateForm.setFieldsValue({
      schedule_name: schedule.schedule_name,
      start_time: dayjs(schedule.start_time),
      end_time: dayjs(schedule.end_time),
      capacity_initial: schedule.capacity_initial,
      schedule_location_id: schedule.schedule_location_id || undefined,
    });
  };

  // 打开创建模态框
  const openCreateModal = (date: Dayjs) => {
    setSelectedDate(date);
    setCreateModalVisible(true);
  };

  // 按日期分组日程
  const groupSchedulesByDate = () => {
    const grouped: Record<string, DoctorSchedule[]> = {};
    
    schedules.forEach(schedule => {
      const date = dayjs(schedule.start_time).format('YYYY-MM-DD');
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(schedule);
    });
    
    return grouped;
  };

  // 生成日历
  const generateCalendar = () => {
    // 获取当前月份的第一天和最后一天
    const startOfMonth = currentMonth.startOf('month');
    const endOfMonth = currentMonth.endOf('month');
    
    // 获取日历开始和结束日期
    const startDay = startOfMonth.startOf('week');
    
    const endDay = endOfMonth.endOf('week');
    
    // 生成日历日期数组
    const calendarDays = [];
    let currentDay = startDay.clone();
    
    while (currentDay.isBefore(endDay) || currentDay.isSame(endDay, 'day')) {
      calendarDays.push(currentDay.clone());
      currentDay = currentDay.add(1, 'day');
    }
    
    // 将日期按周分组
    const weeks = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      weeks.push(calendarDays.slice(i, i + 7));
    }
    
    return weeks;
  };

  const calendarWeeks = generateCalendar();

  // 切换月份
  const handleMonthChange = (date: Dayjs | null) => {
    if (date) {
      setCurrentMonth(date);
    }
  };

  // 检查日期是否已过期（不含当天）
  const isDateExpired = (date: Dayjs) => {
    const today = dayjs().startOf('day');
    return date.isBefore(today);
  };

  return (
    <div>
      <div>
        <h2>日程管理</h2>
      </div>
      <Flex gap="middle" justify="flex-start">
        <Space.Compact>
          <Button onClick={() => handleMonthChange(currentMonth.subtract(1, 'month'))}>{"<"}</Button>
          <DatePicker
            picker="month"
            value={currentMonth}
            onChange={handleMonthChange}
            allowClear={false}
          />
          <Button onClick={() => handleMonthChange(currentMonth.add(1, 'month'))}>{'>'}</Button>
        </Space.Compact>
        <Button color='primary' variant='outlined' onClick={() => setTemplateGenerateModalVisible(true)}>根据模板生成</Button>
        <Button color='danger' variant='outlined' onClick={() => setBatchDeleteModalVisible(true)}>批量删除日程</Button>
      </Flex>
      <div>
        <Row>
          {['周一', '周二', '周三', '周四', '周五', '周六', '周日'].map((day, index) => (
            <Col span={3} key={index} style={{ textAlign: 'center', fontWeight: 'bold', padding: 8 }}>
              {day}
            </Col>
          ))}
        </Row>
        
        {/* 日历内容 */}
        {calendarWeeks.map((week, weekIndex) => (
          <Row key={weekIndex} style={{ minHeight: 120 }}>
            {week.map((day, dayIndex) => {
              const dateStr = day.format('YYYY-MM-DD');
              const daySchedules = groupSchedulesByDate()[dateStr] || [];
              const isCurrentMonth = day.isSame(currentMonth, 'month');
              const isExpired = isDateExpired(day);
              
              return (
                <Col 
                  span={3} 
                  key={dayIndex}
                  style={{ 
                    border: '1px solid #f0f0f0', 
                    backgroundColor: day.isSame(dayjs(), 'day') 
                      ? '#ADD8E6'  // 浅蓝色 - 今天
                      : (day.day() === 6 || day.day() === 0) 
                        ? '#fafafa'  // 浅灰色 - 周六和周日
                        : isCurrentMonth 
                          ? '#fff' 
                          : '#f3f3f3',
                    padding: 4
                  }}
                >
                  <div style={{ 
                    textAlign: 'right', 
                    padding: '4px', 
                    color: isCurrentMonth ? '#000' : '#ccc',
                    fontWeight: day.isSame(dayjs(), 'day') ? 'bold' : 'normal'
                  }}>
                    {day.date()}
                  </div>
                  <div style={{ minHeight: 80 }}>
                    {daySchedules.map(schedule => (
                      <Card 
                        key={schedule.id} 
                        size="small" 
                        style={{ marginBottom: 4 }}
                      >
                        <Popover 
                              content={
                                <Space size="small">
                                  <Button 
                                    type="link" 
                                    icon={<EditOutlined />} 
                                    onClick={() => openUpdateModal(schedule)}
                                    size="small"
                                    disabled={isExpired}
                                  />
                                  <Button 
                                    type="link" 
                                    icon={<DeleteOutlined />} 
                                    danger
                                    onClick={() => handleDelete(schedule.id)}
                                    size="small"
                                    disabled={isExpired}
                                  />
                                </Space>
                              }
                              trigger="hover"
                            >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text strong>{schedule.schedule_name}</Text>
                            <Text>已约{schedule.capacity_initial - schedule.capacity_left}/{schedule.capacity_initial}</Text>
                          </div>
                          {schedule.schedule_location && (
                            <div><Text type="secondary"><EnvironmentOutlined /> {schedule.schedule_location.name}</Text></div>
                          )}
                          <div><Text type="secondary"><ClockCircleOutlined /> {dayjs(schedule.start_time).format('HH:mm')} - {dayjs(schedule.end_time).format('HH:mm')}</Text></div>
                        </Popover>
                      </Card>
                    ))}
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Button 
                      type="link" 
                      icon={<PlusOutlined />} 
                      onClick={() => openCreateModal(day)}
                      size="small"
                      disabled={isExpired}
                    >
                      新建
                    </Button>
                  </div>
                </Col>
              );
            })}
          </Row>
        ))}
      </div>

      {/* 创建日程模态框 */}
      <Modal
        title="新建日程"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText="创建"
        cancelText="取消"
      >
        <Form form={form} layout="vertical" initialValues={{capacity_initial: 1}} onFinish={handleCreateSubmit}>
          <Form.Item>
            日期: {selectedDate.format('YYYY-MM-DD')}
          </Form.Item>

          <Form.Item>
            <Space>
              <Button size='small' color='primary' variant='filled' onClick={() => {
                form.setFieldsValue({
                  schedule_name: '上午',
                  start_time: dayjs('09:00', 'HH:mm'),
                  end_time: dayjs('12:00', 'HH:mm')
                });
              }}>
                上午
              </Button>
              <Button size='small' color='primary' variant='filled' onClick={() => {
                form.setFieldsValue({
                  schedule_name: '下午',
                  start_time: dayjs('13:00', 'HH:mm'),
                  end_time: dayjs('17:00', 'HH:mm')
                });
              }}>
                下午
              </Button>
              <Button size='small' color='primary' variant='filled' onClick={() => {
                form.setFieldsValue({
                  schedule_name: '全天',
                  start_time: dayjs('09:00', 'HH:mm'),
                  end_time: dayjs('17:00', 'HH:mm')
                });
              }}>
                全天
              </Button>
            </Space>
          </Form.Item>

          <Form.Item
            name="schedule_name"
            label="日程名称"
            rules={[{ required: true, message: '请输入日程名称' }]}
          >
            <Input placeholder="请输入日程名称" />
          </Form.Item>

          <Form.Item
            name="start_time"
            label="开始时间"
            rules={[{ required: true, message: '请选择开始时间' }]}
          >
            <TimePicker format="HH:mm" placeholder="请选择开始时间" style={{ width: '100%' }} />
          </Form.Item>
          
          <Form.Item
            name="end_time"
            label="结束时间"
            rules={[{ required: true, message: '请选择结束时间' }]}
          >
            <TimePicker format="HH:mm" placeholder="请选择结束时间" style={{ width: '100%' }} />
          </Form.Item>
          
          <Form.Item
            name="capacity_initial"
            label="可预约人数"
            rules={[{ required: true, message: '请输入可预约人数' }]}
          >
            <Space.Compact>
              <Button 
                type="default" 
                onClick={() => {
                  const currentValue = form.getFieldValue('capacity_initial') || 1;
                  const newValue = Math.max(1, parseInt(currentValue) - 1);
                  form.setFieldsValue({ capacity_initial: newValue });
                }}
              >
                -
              </Button>
              <Form.Item noStyle name="capacity_initial">
                <Input style={{ width: '20%' }} />
              </Form.Item>
              <Button 
                type="default" 
                onClick={() => {
                  const currentValue = form.getFieldValue('capacity_initial') || 1;
                  const newValue = parseInt(currentValue) + 1;
                  form.setFieldsValue({ capacity_initial: newValue });
                }}
              >
                +
              </Button>
            </Space.Compact>
          </Form.Item>
          <Form.Item
            name="schedule_location_id"
            label="日程地点"
          >
            <Select 
              placeholder="请选择日程地点"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {scheduleLocations.map(location => (
                <Select.Option key={location.id} value={location.id}>
                  {location.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 更新日程模态框 */}
      <Modal
        title="编辑日程"
        open={updateModalVisible}
        onCancel={() => {
          setUpdateModalVisible(false);
          updateForm.resetFields();
          setCurrentSchedule(null);
        }}
        onOk={() => updateForm.submit()}
        okText="更新"
        cancelText="取消"
      >
        <Form form={updateForm} layout="vertical" onFinish={handleUpdateSubmit}>
          <Form.Item>
            日期: {currentSchedule ? dayjs(currentSchedule.start_time).format('YYYY-MM-DD') : ''}
          </Form.Item>

          <Form.Item
            name="schedule_name"
            label="日程名称"
            rules={[{ required: true, message: '请输入日程名称' }]}
          >
            <Input placeholder="请输入日程名称" />
          </Form.Item>

          <Form.Item
            name="start_time"
            label="开始时间"
            rules={[{ required: true, message: '请选择开始时间' }]}
          >
            <TimePicker format="HH:mm" placeholder="请选择开始时间" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="end_time"
            label="结束时间"
            rules={[{ required: true, message: '请选择结束时间' }]}
          >
            <TimePicker format="HH:mm" placeholder="请选择结束时间" style={{ width: '100%' }} />
          </Form.Item>
          
          <Form.Item
            name="capacity_initial"
            label="可预约人数"
            rules={[{ required: true, message: '请输入可预约人数' }]}
          >
            <Space.Compact>
              <Button 
                type="default" 
                onClick={() => {
                  const currentValue = updateForm.getFieldValue('capacity_initial') || 1;
                  const newValue = Math.max(1, parseInt(currentValue) - 1);
                  updateForm.setFieldsValue({ capacity_initial: newValue });
                }}
              >
                -
              </Button>
              <Form.Item noStyle name="capacity_initial">
                <Input style={{ width: '20%' }} />
              </Form.Item>
              <Button 
                type="default" 
                onClick={() => {
                  const currentValue = updateForm.getFieldValue('capacity_initial') || 1;
                  const newValue = parseInt(currentValue) + 1;
                  updateForm.setFieldsValue({ capacity_initial: newValue });
                }}
              >
                +
              </Button>
            </Space.Compact>
          </Form.Item>
          <Form.Item
            name="schedule_location_id"
            label="日程地点"
          >
            <Select 
              placeholder="请选择日程地点"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {scheduleLocations.map(location => (
                <Select.Option key={location.id} value={location.id}>
                  {location.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量删除日程模态框 */}
      <Modal
        title="批量删除日程"
        open={batchDeleteModalVisible}
        onCancel={() => {
          setBatchDeleteModalVisible(false);
          batchDeleteForm.resetFields();
        }}
        onOk={() => {
          batchDeleteForm.validateFields().then(async (values) => {
            const startDate = values.dateRange[0];
            const endDate = values.dateRange[1];
            
            if (startDate && endDate) {
              const schedules = await fetchSchedulesByDateRange(startDate, endDate);
              const filteredSchedules = filterSchedulesWithoutAppointments(schedules);
              setSchedulesToDelete(filteredSchedules);
              setConfirmDeleteModalVisible(true);
            }
          });
        }}
        okText="查找日程"
        cancelText="取消"
      >
        <Form form={batchDeleteForm} layout="vertical">
          <Form.Item
            name="dateRange"
            label="选择日期范围"
            rules={[{ required: true, message: '请选择日期范围' }]}
          >
            <DatePicker.RangePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 确认删除日程模态框 */}
      <Modal
        title="确认删除日程"
        open={confirmDeleteModalVisible}
        onCancel={() => setConfirmDeleteModalVisible(false)}
        onOk={handleBatchDeleteConfirm}
        okText="确认删除"
        cancelText="取消"
      >
        <p>以下日程将被删除：</p>
        <SchedulePreviewList 
          schedules={schedulesToDelete} 
        />
        <p>共 <Text strong>{schedulesToDelete.length}</Text> 个日程</p>
      </Modal>

      {/* 根据模板生成排班模态框 */}
      <Modal
        title="根据模板生成排班"
        open={templateGenerateModalVisible}
        onCancel={() => {
          setTemplateGenerateModalVisible(false);
          templateGenerateForm.resetFields();
        }}
        onOk={handlePreviewByTemplate}
        okText="预览"
        cancelText="取消"
      >
        <Form form={templateGenerateForm} layout="vertical">
          <Form.Item
            name="dateRange"
            label="选择日期范围"
            rules={[{ required: true, message: '请选择日期范围' }]}
          >
            <DatePicker.RangePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 预览排班模态框 */}
      <Modal
        title="预览排班"
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        onOk={handleCreateByTemplate}
        okText="确认创建"
        cancelText="取消"
      >
        <p>以下排班将被创建：</p>
        <SchedulePreviewList 
          schedules={templateSchedules} 
        />
        <p>共 <Text strong>{templateSchedules.length}</Text> 个排班</p>
      </Modal>
    </div>
  );
};

export default Schedules;